import { UMIcon } from '../../urbanmap/_components';
import { fmt } from './domain';
import { PC, UMR, caption } from './tokens';

export type CopyStatus = 'idle' | 'copying' | 'done' | 'error';

interface Props {
  name: string;
  perimeterLabel: string;
  buildingCount: number;
  modifiedRatios: number;
  stationName: string;
  djuReference: number;
  coefficient: number;
  status: CopyStatus;
  progress: number;
}

const COPY_STEPS = ['Copie des bâtiments UrbanMap', 'Application des ratios du projet', 'Correction climatique (DJU)', 'Création du projet'];

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '10px 0', borderBottom: `1px solid ${UMR.borderSubtle}` }}>
      <span style={{ color: UMR.textSecondary }}>{label}</span>
      <strong style={{ textAlign: 'right' }}>{value}</strong>
    </div>
  );
}

/** Récapitulatif avant copie, puis progression de la copie (en cours / terminé). */
export function StepRecap(p: Props) {
  const copying = p.status !== 'idle';
  return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 32, overflowY: 'auto', height: '100%', boxSizing: 'border-box' }}>
      <div style={{ width: 560, display: 'flex', flexDirection: 'column', gap: 24, fontSize: 14 }}>
        <section>
          <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 8 }}>Récapitulatif</div>
          <Row label="Nom" value={p.name} />
          <Row label="Périmètre" value={p.perimeterLabel} />
          <Row label="Bâtiments copiés depuis UrbanMap" value={fmt(p.buildingCount)} />
          <Row label="Ratios modifiés" value={p.modifiedRatios === 0 ? 'Aucun (valeurs par défaut)' : `${p.modifiedRatios} sur 30`} />
          <Row label="Station météo · DJU de référence" value={`${p.stationName} · ${fmt(p.djuReference)} °C·j (× ${fmt(p.coefficient, 3)})`} />
          <div style={{ ...caption, marginTop: 8 }}>
            Les bâtiments sont copiés et figés à la date du jour. La synchronisation avec UrbanMap se fera ensuite manuellement, depuis le projet.
          </div>
        </section>

        {copying && (
          <section aria-live="polite" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700 }}>
              <span>{p.status === 'done' ? 'Projet créé' : p.status === 'error' ? 'La création a échoué' : 'Copie en cours…'}</span>
              <span>{Math.round(p.progress)} %</span>
            </div>
            <div style={{ height: 6, background: UMR.grey100 }}>
              <div style={{ height: '100%', width: `${p.progress}%`, background: p.status === 'error' ? PC.dangerText : PC.accent, transition: 'width 200ms' }} />
            </div>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
              {COPY_STEPS.map((s, i) => {
                const done = p.progress >= ((i + 1) / COPY_STEPS.length) * 100;
                return (
                  <li key={s} style={{ display: 'flex', alignItems: 'center', gap: 8, color: done ? UMR.black : UMR.textSecondary }}>
                    <UMIcon name={done ? 'check_circle' : 'radio_button_unchecked'} size={18} color={done ? PC.accent : UMR.textSecondary} />
                    {i === 0 ? `${s} (${fmt(p.buildingCount)})` : s}
                  </li>
                );
              })}
            </ul>
          </section>
        )}
      </div>
    </div>
  );
}
